import { redirect, type ParsedLocation } from '@tanstack/react-router';
import type { ContextoDoRoteador } from '@/app/router';
import { eErroDaApi } from '@/lib/api/erros';
import { consultaDoMe } from './dados/me';
import type { Me } from './sessao';

type Contexto = Pick<ContextoDoRoteador, 'queryClient' | 'api'>;

/**
 * Lê o `/me` para uma guarda de rota. Sem sessão (401), devolve `null`, sem passar pelo tratamento
 * central de sessão expirada: aqui o 401 é a resposta esperada para quem ainda não entrou.
 */
export async function lerSessao({ queryClient, api }: Contexto): Promise<Me | null> {
  const consulta = consultaDoMe(api);
  const emCache = queryClient.getQueryData(consulta.queryKey);
  // Invalidado (ex. depois de ativar o 2FA): as pendências podem ter mudado; busca de novo.
  const invalidado = queryClient.getQueryState(consulta.queryKey)?.isInvalidated === true;
  if (emCache !== undefined && !invalidado) return emCache;
  try {
    const { data } = await api.GET('/api/v1/me');
    if (data === undefined) return null;
    queryClient.setQueryData(consulta.queryKey, data);
    return data;
  } catch (erro) {
    if (eErroDaApi(erro) && erro.status === 401) return null;
    throw erro;
  }
}

/** Rotas da equipe: sem sessão vai ao login com `volta` (CA-20); com 2FA pendente, à ativação. */
export async function exigirSessao(contexto: Contexto, local: ParsedLocation): Promise<Me> {
  const me = await lerSessao(contexto);
  if (me === null) throw redirect({ to: '/entrar', search: { volta: local.href } });
  if (me.pendencias.includes('ATIVAR_2FA')) throw redirect({ to: '/ativar-2fa' });
  return me;
}
